import { nil, UIColor, UITextView, UIView, UIViewAddControlEventTargetObject } from "uicore-ts"
import { RHColor } from "./RHColor"

export interface RHDropdownOption<T = unknown> {
    label: string
    value: T
    disabled?: boolean
}

/**
 * A Bootstrap 5.3-styled dropdown component using a native HTML select element
 */
export class RHDropdown<T = unknown> extends UIView {
    
    static override controlEvent = Object.assign({}, UIView.controlEvent, {
        "SelectionDidChange": "SelectionDidChange"
    })
    
    // @ts-ignore
    get controlEventTargetAccumulator(): UIViewAddControlEventTargetObject<typeof RHDropdown> {
        return super.controlEventTargetAccumulator as UIViewAddControlEventTargetObject<typeof RHDropdown>
    }
    
    // Label for the dropdown
    readonly labelView = new UITextView()
        .configuredWithObject({
            text: "",
            backgroundColor: UIColor.transparentColor,
            style: {
                fontSize: "14px",
                fontWeight: "500",
                color: RHColor.textPrimary.stringValue,
                marginBottom: "4px"
            }
        })
    
    // The native select element wrapper
    private _selectElement: HTMLSelectElement
    private _options: RHDropdownOption<T>[] = []
    private _placeholderText?: string
    private _labelText: string = ""
    _appliedSelectTop?: number
    _appliedSelectWidth?: number
    _appliedSelectHeight?: number
    
    constructor(elementID?: string) {
        super(elementID)
        
        // Create native select element
        this._selectElement = document.createElement("select")
        this._selectElement.className = "form-select"
        
        // Base sizing/background only - border and focus-ring styling are left
        // to the CDN-loaded Bootstrap 5.3 ".form-select"/".form-select:focus"
        // stylesheet rules on the className set above. An inline border used
        // to be set here too, with JS focus/blur listeners hand-rolling the
        // same ring Bootstrap already provides - but an inline style always
        // wins over a stylesheet :focus rule, so those listeners had to
        // rewrite this element's own inline style on every focus/blur. On
        // macOS, Chrome renders <select> popups as a native control that
        // dismisses itself the instant the focused select's own inline style
        // is mutated at all (regardless of whether the change is geometric),
        // which closed the dropdown right after it opened.
        Object.assign(this._selectElement.style, {
            display: "block",
            width: "100%",
            padding: "0.375rem 2.25rem 0.375rem 0.75rem",
            fontSize: "1rem",
            fontWeight: "400",
            lineHeight: "1.5",
            color: "#212529",
            backgroundColor: "#fff",
            appearance: "none",
            backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3e%3c/svg%3e")`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "right 0.75rem center",
            backgroundSize: "16px 12px"
        })

        // Change event
        this._selectElement.addEventListener("change", () => {
            this.selectionDidChange()
        })
        
        // Append to view's DOM element
        this.viewHTMLElement.appendChild(this._selectElement)
        
        // Add label as subview
        this.labelView.addedAsSubviewToView(this)
    }
    
    /**
     * Label text property
     */
    get labelText(): string {
        return this._labelText
    }
    
    set labelText(text: string) {
        this._labelText = text
        this.labelView.text = text
        this.labelView.hidden = !text || text.length === 0
        this.setNeedsLayout()
    }
    
    /**
     * Options property
     */
    get options(): RHDropdownOption<T>[] {
        return this._options
    }
    
    set options(options: RHDropdownOption<T>[]) {
        this._options = options
        this.updateOptionsInSelect()
    }
    
    /**
     * Placeholder text property
     */
    get placeholderText(): string | undefined {
        return this._placeholderText
    }
    
    set placeholderText(text: string | undefined) {
        this._placeholderText = text
        this.updateOptionsInSelect()
    }
    
    /**
     * Selected value property
     */
    get selectedValue(): T | undefined {
        const selectedIndex = this._selectElement.selectedIndex
        const hasPlaceholder = this._selectElement.options[0]?.disabled
        
        if (selectedIndex < 0 || (hasPlaceholder && selectedIndex === 0)) {
            return undefined
        }
        
        const optionIndex = hasPlaceholder ? selectedIndex - 1 : selectedIndex
        return this._options[optionIndex]?.value
    }
    
    set selectedValue(value: T | undefined) {
        if (value === undefined) {
            this._selectElement.selectedIndex = 0
            return
        }
        
        const index = this._options.findIndex(opt => opt.value === value)
        if (index >= 0) {
            const hasPlaceholder = this._selectElement.options[0]?.disabled
            this._selectElement.selectedIndex = index + (hasPlaceholder ? 1 : 0)
        }
    }
    
    /**
     * Selected option property
     */
    get selectedOption(): RHDropdownOption<T> | undefined {
        const selectedIndex = this._selectElement.selectedIndex
        const hasPlaceholder = this._selectElement.options[0]?.disabled
        
        if (selectedIndex < 0 || (hasPlaceholder && selectedIndex === 0)) {
            return undefined
        }
        
        const optionIndex = hasPlaceholder ? selectedIndex - 1 : selectedIndex
        return this._options[optionIndex]
    }
    
    /**
     * Enabled property
     */
    override set enabled(value: boolean) {
        this._selectElement.disabled = !value
        if (!value) {
            this._selectElement.style.backgroundColor = "#e9ecef"
            this._selectElement.style.opacity = "0.65"
        } else {
            this._selectElement.style.backgroundColor = "#fff"
            this._selectElement.style.opacity = "1"
        }
    }
    
    override get enabled(): boolean {
        return !this._selectElement.disabled
    }
    
    /**
     * Check if a valid option is selected
     */
    get hasValidSelection(): boolean {
        return this.selectedValue !== undefined
    }
    
    /**
     * Reset to placeholder/first option
     */
    reset() {
        this._selectElement.selectedIndex = 0
    }
    
    /**
     * Update options in select element
     */
    private updateOptionsInSelect() {
        // Clear existing options
        this._selectElement.innerHTML = ""
        
        // Add placeholder option if provided
        if (this._placeholderText) {
            const placeholderOption = document.createElement("option")
            placeholderOption.value = ""
            placeholderOption.textContent = this._placeholderText
            placeholderOption.disabled = true
            placeholderOption.selected = true
            this._selectElement.appendChild(placeholderOption)
        }
        
        // Add all options
        this._options.forEach((opt, index) => {
            const optionElement = document.createElement("option")
            optionElement.value = index.toString()
            optionElement.textContent = opt.label
            if (opt.disabled) {
                optionElement.disabled = true
            }
            this._selectElement.appendChild(optionElement)
        })
    }
    
    /**
     * Selection changed handler
     */
    private selectionDidChange() {
        this.sendControlEventForKey(RHDropdown.controlEvent.SelectionDidChange, nil)
    }
    
    
    override layoutSubviews() {
        super.layoutSubviews()
        
        const bounds = this.bounds
        let selectTop = 0
        
        if (!this.labelView.hidden) {
            // Layout label at top
            const labelHeight = this.labelView.intrinsicContentHeight(bounds.width)
            this.labelView.frame = bounds.rectangleWithHeight(labelHeight)
            selectTop = labelHeight + 4
        }

        // The native <select> is a raw DOM child, not a UIView subview, so its
        // position is written directly here rather than through the frame
        // system - which means it isn't covered by the frame system's own
        // isVirtualLayouting guard. Skipping this during virtual passes
        // matters because ancestors recompute intrinsic height (and so call
        // layoutIfNeeded on this view) far more often than real layout
        // happens; rewriting the select's own inline position style while it
        // has focus makes Chrome close its open native dropdown, so the
        // picker appeared to open and immediately close on click.
        if (this.isVirtualLayouting) {
            return
        }

        const selectHeight = bounds.height - selectTop

        // The framework re-runs layoutSubviews multiple times per real pass
        // while it settles (see UIView.layoutPassMaxIterations). Rewriting
        // identical inline style values on every iteration still closes an
        // open native dropdown (see the isVirtualLayouting note above), so
        // the DOM write is skipped whenever the geometry actually did not
        // change.
        if (this._appliedSelectTop === selectTop &&
            this._appliedSelectWidth === bounds.width &&
            this._appliedSelectHeight === selectHeight) {
            return
        }
        this._appliedSelectTop = selectTop
        this._appliedSelectWidth = bounds.width
        this._appliedSelectHeight = selectHeight

        this._selectElement.style.position = "absolute" // project-rules-ignore no-layout-style-assignment
        this._selectElement.style.top = `${selectTop}px` // project-rules-ignore no-layout-style-assignment
        this._selectElement.style.left = "0" // project-rules-ignore no-layout-style-assignment
        this._selectElement.style.width = `${bounds.width}px` // project-rules-ignore no-layout-style-assignment
        this._selectElement.style.height = `${selectHeight}px` // project-rules-ignore no-layout-style-assignment
    }
    
    /**
     * Get intrinsic height including label
     */
    override intrinsicContentHeight(width: number): number {
        const labelHeight = this.labelView.hidden ? 0 : this.labelView.intrinsicContentHeight(width) + 4
        const selectHeight = 38 // Standard Bootstrap select height
        return labelHeight + selectHeight
    }
    
}
